import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  TextField,
  Grid,
  Container,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import ShepButton from "@/components/GenericComponents/ShepButton";
import { useDispatch } from "react-redux";
import { handleAddCatalog } from "@/features/connections/manageCatalogDataSlice";
import { defaultCode } from "@/features/connections/integrationSlice";
import axios from "@/features/axiosHelper";
//SHEP-940 Duplicate of AddItemPopup with a couple tweaks
//required since the original required being used within a grid
//removed EDI, qualifiers, states, and grid context since this should only be handling email connections

const EmailAddItemPopup = ({
  open,
  onClose,
  onSuccess,
  initialItemCode = "",
  initialItemDesc = "",
  connectionId,
  connectionName = "",
  artifactType = 2002,
}) => {
  const dispatch = useDispatch();
  const [dataValues, setDataValues] = useState({});
  const [itemCode, setItemCode] = useState("");
  const [saveAttempted, setSaveAttempted] = useState(false);

  useEffect(() => {
    if (open) {
      setItemCode(initialItemCode);
      setDataValues((prev) => ({ ...prev, ItemDesc: initialItemDesc }));
      setSaveAttempted(false);
    }
  }, [open, initialItemCode, initialItemDesc]);

  const validateForm = () => {
    if (itemCode === defaultCode) {
      return {
        isValid: false,
        message: `Item Code cannot be saved as ${defaultCode}`,
      };
    }
    if (!itemCode || itemCode.trim() === "") {
      return {
        isValid: false,
        message: "Item Code is required.",
      };
    }
    return { isValid: true };
  };

  const handleClose = () => {
    onClose();
    setDataValues({});
    setItemCode("");
    setSaveAttempted(false);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setDataValues((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const fetchCatalog = async () => {
    try {
      const response = await axios.get("/email/catalogs", {
        params: {
          connectionId: connectionId,
          dataType: artifactType,
          maxCount: 1,
          search: itemCode,
          parentCatalogId: null,
        },
      });
      const results = response.data.Records;
      return results.length > 0 ? response.data.Records[0] : null;
    } catch (error) {
      console.error("Error fetching new catalog: ", error);
      return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={"xl"}
      fullWidth={true}
      PaperProps={{ style: { borderRadius: 15 } }}
    >
      <DialogTitle
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "var(--primary-main)",
          color: "var(--default-light)",
          fontSize: "24px",
          borderRadius: "15px 15px 0 0",
          padding: "16px 24px",
        }}
      >
        <div style={{ fontWeight: "var(--font-semi-bold)" }}>
          {"Add Catalog - Item for " + (connectionName || "Connection")}
        </div>
        <IconButton
          onClick={handleClose}
          className="closeIconButton"
          style={{ color: "var(--default-light)" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        className="dialogContent"
        style={{
          backgroundColor: "var(--disabled)",
          padding: "35px",
          minWidth: 400,
        }}
      >
        <Container>
          <Grid
            container
            spacing={2}
            rowSpacing={1}
            style={{ marginBottom: "20px", marginTop: "20px" }}
            sx={{
              px: 20,
            }}
          >
            <Grid item xs={4}>
              <Typography>Item Code</Typography>
              <Typography>
                <TextField
                  variant="outlined"
                  name="ItemCode"
                  value={itemCode}
                  onChange={(e) => setItemCode(e.target.value)}
                  error={saveAttempted && (!itemCode || itemCode.trim() === "")}
                  sx={{
                    width: 250,
                    marginTop: 1,
                    marginBottom: 2,
                  }}
                />
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography>Item Description</Typography>
              <Typography>
                <TextField
                  variant="outlined"
                  name="ItemDesc"
                  value={dataValues?.ItemDesc || ""}
                  onChange={handleChange}
                  sx={{
                    width: 250,
                    marginTop: 1,
                    marginBottom: 2,
                  }}
                />
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography>Weight</Typography>
              <Typography>
                <TextField
                  variant="outlined"
                  name="Weight"
                  type="number"
                  value={dataValues?.Weight ?? ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setDataValues((prevData) => ({
                      ...prevData,
                      Weight: value === "" ? null : Number(value),
                    }));
                  }}
                  sx={{
                    width: 250,
                    marginTop: 1,
                    marginBottom: 2,
                  }}
                />
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </DialogContent>
      <DialogActions style={{ padding: "16px 24px" }}>
        <ShepButton variant="secondary" onClick={handleClose}>
          Cancel
        </ShepButton>
        <ShepButton
          onClick={async () => {
            setSaveAttempted(true);
            const onSuccessfulSave = async () => {
              handleClose();
              if (onSuccess) {
                const newCatalog = await fetchCatalog();
                onSuccess(newCatalog);
              }
            };
            await handleAddCatalog(
              validateForm,
              onSuccessfulSave,
              dispatch,
              connectionId,
              artifactType,
              JSON.stringify({
                ...dataValues,
                connectionName,
                ItemCode: itemCode,
              }),
              {},
            );
          }}
        >
          Add
        </ShepButton>
      </DialogActions>
    </Dialog>
  );
};

export default EmailAddItemPopup;
